import {
    Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    FormControl, IconButton, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead,
    TablePagination, TableRow, TextField, Tooltip, Typography
} from "@mui/material"
import { Refresh } from "@mui/icons-material"
import { FC, useEffect, useState } from "react"
import PageTitle from "../Common/PageTitle"
import NoData from "../Common/NoData"
import { Apis } from "../../helpers"
import { TABLE_HEIGHT } from "../../configs"
import { EventsEmit } from "../../configs/events"

// Issues guests report about a BnB stay from the mobile app (collection "bnb_reported_issues"). Super admin only, like the lock
// store's Orders page - see modules/bnb.ts.
type IssueStatus = 'Open' | 'Resolved'

interface ReportedIssue {
    _id: string
    date: string
    category: string
    message: string
    status: IssueStatus
    resolvedAt?: string
    resolutionNote?: string
    contact?: { name?: string, phone?: string }
    guest?: { firstName?: string, lastName?: string, phone?: string, email?: string } | null
    snapshot?: { propertyName?: string, unitName?: string, unitLabel?: string, place?: string, checkInDate?: string, checkOutDate?: string, amount?: number }
}

const ksh = (amount?: number) => `Ksh ${Math.round(amount || 0).toLocaleString('en-US')}`
const formatDateTime = (date?: string) =>
    date ? new Date(date).toLocaleString('en-KE', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'
const formatDate = (date?: string) => date ? new Date(date).toLocaleDateString('en-KE', { year: 'numeric', month: 'short', day: 'numeric' }) : '—'
const toast = (type: 'success' | 'error', message: string) => EventsEmit('modals', { action: 'toastMsg', type, message })

interface StateProps {
    issues: ReportedIssue[]
    loading: boolean
    refreshing: boolean
    status: IssueStatus | 'all'
    page: number
    pageSize: number
    count: number
    resolving: ReportedIssue | null
    note: string
    saving: boolean
}

const AdminBnBReportedIssues: FC = () => {
    const [state, setState] = useState<StateProps>({
        issues: [], loading: true, refreshing: false, status: 'Open', page: 0, pageSize: 25, count: 0, resolving: null, note: '', saving: false
    })
    const { issues, loading, refreshing, status, page, pageSize, count, resolving, note, saving } = state

    const fetchIssues = ({ newStatus, newPage, newPageSize, processVariable }: any = {}) => {
        const Status = newStatus || state.status
        const Page = newPage != null && newPage > -1 ? newPage : state.page
        const PageSize = newPageSize || state.pageSize
        if (processVariable) setState(s => ({ ...s, [processVariable]: true }))
        const afterLoad = { loading: false, refreshing: false, status: Status, page: Page, pageSize: PageSize }
        Apis({ module: 'BnB', action: 'getBnBReportedIssues', status: Status === 'all' ? undefined : Status, page: Page, pageSize: PageSize })
            .then((resp: any) => {
                if (resp.error) toast('error', resp.message)
                setState(s => resp.error ? { ...s, ...afterLoad } : { ...s, ...afterLoad, issues: resp.data || [], count: resp.count ?? s.count })
            })
            .catch(() => setState(s => ({ ...s, ...afterLoad })))
    }

    useEffect(() => { fetchIssues({ processVariable: 'loading' }) }, [])

    const resolve = () => {
        if (!resolving) return
        setState(s => ({ ...s, saving: true }))
        Apis({ module: 'BnB', action: 'resolveBnBReportedIssue', issueId: resolving._id, note })
            .then((resp: any) => {
                toast(resp.error ? 'error' : 'success', resp.message)
                setState(s => ({
                    ...s, saving: false, resolving: null, note: '',
                    issues: resp.error ? s.issues : s.issues.map(i => i._id === resolving._id ? { ...i, ...resp.issue } : i)
                }))
            })
            .catch(() => setState(s => ({ ...s, saving: false })))
    }

    const headCell = { fontWeight: 'bold', fontSize: 13 }
    const cell = { fontSize: 13, verticalAlign: 'top' }

    return (
        <Box sx={{ pr: 5, height: '100%' }}>

            <PageTitle title="BnB Reported Issues" />

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, mt: 3, gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    {count > 0 && <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{count.toLocaleString()} {count === 1 ? 'report' : 'reports'}</Typography>}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <Select
                            value={status}
                            onChange={e => fetchIssues({ newStatus: e.target.value, newPage: 0, processVariable: 'loading' })}
                            sx={{ fontSize: 13, borderRadius: 2 }}
                        >
                            <MenuItem sx={{ fontSize: 13 }} value="all">All statuses</MenuItem>
                            <MenuItem sx={{ fontSize: 13 }} value="Open">Open</MenuItem>
                            <MenuItem sx={{ fontSize: 13 }} value="Resolved">Resolved</MenuItem>
                        </Select>
                    </FormControl>
                    <Tooltip title="Refresh">
                        <IconButton size="small" onClick={() => fetchIssues({ processVariable: 'refreshing' })} disabled={refreshing}>
                            {refreshing ? <CircularProgress size={18} /> : <Refresh sx={{ fontSize: 20 }} />}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {!issues.length || loading ?
                <NoData message={status !== 'all' ? `No ${String(status).toLowerCase()} reports.` : 'No issues have been reported yet.'} loading={loading} /> :
                <Paper sx={{ width: '100%' }} elevation={0} variant="outlined">
                    <TableContainer sx={{ maxHeight: TABLE_HEIGHT }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headCell}>Reported</TableCell>
                                    <TableCell sx={headCell}>Guest</TableCell>
                                    <TableCell sx={headCell}>Stay</TableCell>
                                    <TableCell sx={headCell}>Category</TableCell>
                                    <TableCell sx={headCell}>Message</TableCell>
                                    <TableCell sx={headCell}>Status</TableCell>
                                    <TableCell sx={{ ...headCell, width: 40 }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {issues.map(issue => {
                                    const guestName = issue.guest ? [issue.guest.firstName, issue.guest.lastName].filter(Boolean).join(' ') : null
                                    return (
                                        <TableRow key={issue._id} hover>
                                            <TableCell sx={cell}>{formatDateTime(issue.date)}</TableCell>
                                            <TableCell sx={cell}>
                                                <Box sx={{ fontWeight: 600 }}>{issue.contact?.name || guestName || '—'}</Box>
                                                <Box sx={{ color: 'text.secondary' }}>{issue.contact?.phone || issue.guest?.phone}</Box>
                                                {issue.guest?.email && <Box sx={{ color: 'text.secondary', fontSize: 12 }}>{issue.guest.email}</Box>}
                                            </TableCell>
                                            <TableCell sx={{ ...cell, maxWidth: 220 }}>
                                                <Box sx={{ fontWeight: 600 }}>{[issue.snapshot?.unitName, issue.snapshot?.unitLabel].filter(Boolean).join(' · ') || '—'}</Box>
                                                <Box sx={{ color: 'text.secondary' }}>{issue.snapshot?.propertyName}{issue.snapshot?.place ? ` · ${issue.snapshot.place}` : ''}</Box>
                                                {issue.snapshot?.checkInDate && (
                                                    <Box sx={{ color: 'text.secondary', fontSize: 12 }}>
                                                        {formatDate(issue.snapshot.checkInDate)} – {formatDate(issue.snapshot.checkOutDate)} · {ksh(issue.snapshot.amount)}
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell sx={cell}><Chip size="small" variant="outlined" label={issue.category} /></TableCell>
                                            <TableCell sx={{ ...cell, maxWidth: 320, whiteSpace: 'pre-wrap' }}>{issue.message}</TableCell>
                                            <TableCell sx={cell}>
                                                <Chip size="small" color={issue.status === 'Open' ? 'warning' : 'success'} label={issue.status} />
                                                {issue.status === 'Resolved' && issue.resolutionNote && (
                                                    <Tooltip title={issue.resolutionNote}>
                                                        <Box sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5, maxWidth: 160, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {issue.resolutionNote}
                                                        </Box>
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                            <TableCell sx={cell}>
                                                {issue.status === 'Open' && (
                                                    <Button size="small" variant="outlined" onClick={() => setState(s => ({ ...s, resolving: issue, note: '' }))} sx={{ textTransform: 'none', fontSize: 12 }}>
                                                        Resolve
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={count}
                        page={page}
                        onPageChange={(_, newPage) => fetchIssues({ newPage, processVariable: 'loading' })}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={e => fetchIssues({ newPage: 0, newPageSize: parseInt(e.target.value, 10), processVariable: 'loading' })}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </Paper>
            }

            <Dialog open={Boolean(resolving)} onClose={() => setState(s => ({ ...s, resolving: null }))} maxWidth="sm" fullWidth>
                <DialogTitle>Mark as resolved</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ fontSize: 14, mb: 2 }}>
                        {resolving?.contact?.name || 'The guest'}'s report about "{resolving?.category}" for {resolving?.snapshot?.unitLabel || resolving?.snapshot?.unitName}.
                    </DialogContentText>
                    <TextField
                        label="Resolution note (optional)"
                        placeholder="What was done about it"
                        multiline
                        minRows={3}
                        fullWidth
                        value={note}
                        onChange={e => setState(s => ({ ...s, note: e.target.value }))}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setState(s => ({ ...s, resolving: null }))}>Cancel</Button>
                    <Button variant="contained" disableElevation disabled={saving} onClick={resolve}>
                        {saving ? 'Saving...' : 'Mark resolved'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default AdminBnBReportedIssues
